import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { API_URL } from '../../config'
import { ArtificialDetailPropertyNames } from '../../common/globalNotary'
import permissionManager from '../../services/permissions/PermissionManager'

const sanitizeString = (input) => {
  if (!input) return input
  if (typeof input === 'object') throw new Error('simple value types expected')
  if (typeof input === 'string') return encodeURIComponent(input.trim())
  return input
}
export const massageSingleRecordBeforeUpsert = primaryDetailRecord => {
  const returnValue = { ...primaryDetailRecord }
  if (returnValue.hasOwnProperty('deleted_at')) delete returnValue.deleted_at
  if (returnValue.hasOwnProperty('updated_at')) delete returnValue.updated_at
  if (returnValue.hasOwnProperty('created_at')) delete returnValue.created_at
  if (returnValue.hasOwnProperty('inherits')) delete returnValue.inherits
  if (returnValue.hasOwnProperty('active')) delete returnValue.active
  if (returnValue.hasOwnProperty('undefined')) delete returnValue.undefined

  if (
    primaryDetailRecord.hasOwnProperty('addresses') &&
    primaryDetailRecord.addresses.length
  ) {
    returnValue.addresses = primaryDetailRecord.addresses.map(
      a => ({...a, name: 'generic'})
    )
  }

  delete returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]

  delete returnValue[
    ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
  ]

  delete returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]

  delete returnValue[
    ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
  ]

  delete returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]

  delete returnValue[
    ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
  ]

  delete returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]

  delete returnValue[
    ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
  ]

  delete returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5]

  delete returnValue[
    ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5_INITIAL_VALUES
  ]

  delete returnValue.is_system
  delete returnValue.object_counts
  if (!returnValue.suspended_at) returnValue.suspended_at = null

  for (const property in returnValue) {
    const val = returnValue[property]

    if (val === undefined || val === '') {
      returnValue[property] = null
    }
  }

  if (returnValue.addresses?.length) {
    for (const address of returnValue.addresses) {
      for (const prop in address) {
        const val = address[prop]

        if (val === undefined || val === '') {
          address[prop] = null
        }
      }
    }
  }

  return returnValue
}

export const queryBuilder = (rootUrl, allParametersWrappedInOneObject) => {
  if (!rootUrl || typeof rootUrl !== 'string')
    throw new Error('invalid query parameters')
  let returnVal = rootUrl

  if (!allParametersWrappedInOneObject) return rootUrl

  const parameters = []
  for (const property in allParametersWrappedInOneObject) {
    const oneValue = allParametersWrappedInOneObject[property]
    if (property === 'id') {
      returnVal = returnVal + `/${oneValue}`
    } else if (property === 'ids' && Array.isArray(oneValue)) {
      oneValue.forEach(function (el, idx) {
        parameters.push(`${property}[]=` + sanitizeString(el))
      })
    } else {
      if (oneValue) {
        if (Array.isArray(oneValue)) {
          const arrayToString = oneValue
            .filter(el => el)
            .map(function (el, idx) {
              return `${property}[]=` + sanitizeString(el)
            })
          parameters.push(arrayToString.join('&'))
        } else {
          const simplePropertyToString =
            `${property}=` + sanitizeString(oneValue)
          parameters.push(simplePropertyToString)
        }
      }
    }
  }

  //Temporary fix to the pagination issue until proper pageination logic can be added
  //For now we use the object key 'ids' to identify if its calling to generate a list and add the per_page param
  if (Object.keys(allParametersWrappedInOneObject).includes('ids'))
  {
    if (!Object.keys(allParametersWrappedInOneObject).includes('per_page'))
    {
      parameters.push('per_page=100')
    }
  }

  if (parameters.length === 0) return rootUrl

  for (let i = 0; i < parameters.length; i++) {
    if (i === 0) {
      returnVal += '?'
    } else {
      returnVal += '&'
    }
    returnVal += parameters[i]
  }
  return returnVal
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const dicKey = 'dk_' + window.location.host
    const token = window.localStorage.getItem('token')
    const userId = window.localStorage.getItem('userId')
    if (!token) {
      window.location = '/login'
    }

    const clientKey = JSON.parse(window.localStorage.getItem(dicKey)).key

    headers.set('X-Client-Key', clientKey)
    headers.set('Authorization', `Bearer ${token}`)
    headers.set('userId', userId)
    headers.set('Accept', 'application/json')

    return headers
  },
})

const baseQueryWithInterceptor = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)

  if (result.error) {
    return result
  }

  if (result.data) {
    permissionManager.updatePermissionsFromAPIResponse(result.data)
  }

  return result
}

export const apiContainer = createApi({
  baseQuery: baseQueryWithInterceptor,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: () => ({}),
  cache: null,
})
